/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IOrderTemplateNameDialogResources } from '@msdyn365-commerce/components';
import { deleteAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import { getOrderTemplatesPreview, OrderTemplate } from '@msdyn365-commerce-modules/retail-actions';
import { Alert, Button, Heading, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
// @ts-expect-error importing js
import { createGetOrderTemplatesPreviewInput } from '@msdyn365-commerce-modules/retail-actions/dist/lib/order-templates/get-order-templates-preview';

import { ProductList } from '@msdyn365-commerce/retail-proxy';
import Spinner from '../order-template/components/add-line/spinner';
import {
    ISingleOrderTemplateProps,
    ISingleOrderTemplateViewProps,
    OrderTemplateListStatusMessage,
    OrderTemplateRender
} from './components';
import CreateNewTemplateButtonComponent from './components/create-order-template';
import { IOrderTemplateListData } from './order-template-list.data';
import { HeadingTag, IOrderTemplateListProps, IOrderTemplateListResources } from './order-template-list.props.autogenerated';

export interface IOrderTemplateActionMessageState {
    itemKey: string;
    userMessage: string;
    statusCssString: string;
    messageGlyph: string;
    isOpen: boolean;
    headerStatusMessage: string;
    isContactModelOrderTemplate?: boolean;
    orderTemplatesData?: OrderTemplate[];
    selectedFilter?: string;
}

export type orderTemplateListStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY';

export interface IOrderTemplateListViewProps extends IOrderTemplateListProps<IOrderTemplateListData> {
    OrderTemplateList: IModuleProps;
    status?: orderTemplateListStatus;
    statusMessage?: React.ReactNode;
    heading?: React.ReactNode;
    OrderTemplates?: INodeProps;
    OrderTemplate?: INodeProps;
    orderTemplates?: ISingleOrderTemplateViewProps[];
    createOrderTemplateButton?: React.ReactNode;
    templateHeaderStatusMessage?: React.ReactNode;
    filterButtons?: React.ReactNode;
}

/**
 *
 * OrderTemplateList component.
 * @extends {React.PureComponent<IOrderTemplateListProps<IOrderTemplateListData>, IOrderTemplateListActionMessageState>}
 */
@observer
class OrderTemplateList extends React.Component<IOrderTemplateListProps<IOrderTemplateListData>, IOrderTemplateActionMessageState> {
    constructor(props: IOrderTemplateListProps<IOrderTemplateListData>) {
        super(props);
        this.state = {
            itemKey: '',
            userMessage: '',
            headerStatusMessage: '',
            statusCssString: 'success',
            messageGlyph: 'fas fa-check',
            isOpen: false,
            selectedFilter: '0'
        };
    }

    public async componentDidMount(): Promise<void> {
        const { data, context } = this.props;

        this.setState({
            isContactModelOrderTemplate: (context.request.user as { isB2bContact?: boolean }).isB2bContact === true,
            orderTemplatesData: (await data.orderTemplates) ?? []
        });
    }

    public shouldComponentUpdate(
        nextProps: IOrderTemplateListProps<IOrderTemplateListData>,
        nextState: IOrderTemplateActionMessageState
    ): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { className, heading } = this.props.config;
        const { emptyOrderTemplateText, createNewTemplateButtonText } = this.props.resources;
        const { orderTemplates } = this.props.data;
        const orderTemplatesData = orderTemplates && orderTemplates.result;
        const orderTemplateListHeading = heading?.text ?? this.props.resources.orderTemplateListTitle;

        if (orderTemplates.status === 'LOADING') {
            const errorProps: IOrderTemplateListViewProps = {
                ...(this.props as IOrderTemplateListProps<IOrderTemplateListData>),
                OrderTemplateList: {
                    moduleProps: this.props,
                    className: classnames('ms-order-template', className)
                },
                status: 'LOADING',
                statusMessage: <Spinner className='ms-order-template' msg={this.props.resources.progressNotificationText} />,
                heading: (
                    <Heading className='ms-order-template__heading' tag={heading?.tag ?? HeadingTag.h2} text={orderTemplateListHeading} />
                )
            };
            return this.props.renderView(errorProps);
        }

        if (orderTemplatesData && orderTemplatesData.length === 0) {
            // When no order templates are found for customer
            const emptyViewProps: IOrderTemplateListViewProps = {
                ...(this.props as IOrderTemplateListProps<IOrderTemplateListData>),
                OrderTemplateList: {
                    moduleProps: this.props,
                    className: classnames('ms-order-template', className)
                },
                status: 'EMPTY',
                statusMessage: <OrderTemplateListStatusMessage errorType='EMPTY' text={emptyOrderTemplateText} />,
                createOrderTemplateButton: (
                    <CreateNewTemplateButtonComponent
                        context={this.props.context}
                        id={this.props.id}
                        typeName={this.props.typeName}
                        data={{}}
                        createOrderTemplateText={createNewTemplateButtonText}
                        onOrderTemplateCreated={this._reloadPage}
                        createOrderTemplateDialogResources={this._generateOrderTemplateNameDialogResources(this.props.resources)}
                    />
                ),
                heading: (
                    <Heading className='ms-order-template__heading' tag={heading?.tag ?? HeadingTag.h2} text={orderTemplateListHeading} />
                )
            };

            return this.props.renderView(emptyViewProps);
        }

        const viewProps: IOrderTemplateListViewProps = {
            ...(this.props as IOrderTemplateListProps<IOrderTemplateListData>),
            OrderTemplateList: {
                moduleProps: this.props,
                className: classnames('ms-order-template', className)
            },
            status: 'SUCCESS',
            heading: <Heading className='ms-order-template__heading' tag={heading?.tag ?? HeadingTag.h2} text={orderTemplateListHeading} />,
            createOrderTemplateButton: (
                <CreateNewTemplateButtonComponent
                    context={this.props.context}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{}}
                    createOrderTemplateText={createNewTemplateButtonText}
                    onOrderTemplateCreated={this._reloadPage}
                    createOrderTemplateDialogResources={this._generateOrderTemplateNameDialogResources(this.props.resources)}
                />
            ),
            templateHeaderStatusMessage: this.state.isOpen && this.state.headerStatusMessage && (
                <div className='ms-order-template__products-status'>
                    <Alert color={this.state.statusCssString} isOpen={this.state.isOpen} toggle={this._onDismiss}>
                        <span>{this.state.headerStatusMessage}</span>
                    </Alert>
                </div>
            ),
            OrderTemplates: {
                className: 'ms-order-template__list-item',
                tag: 'ul'
            },
            OrderTemplate: {
                className: 'ms-order-template__tile',
                tag: 'li'
            },
            orderTemplates: orderTemplatesData && this._renderOrderTemplateList(orderTemplatesData),
            filterButtons: this._renderFilterButtons({
                className: 'ms-order-template-list__filter-button',
                resources: this.props.resources
            })
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _generateOrderTemplateNameDialogResources(resources: IOrderTemplateListResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
            orderTemplateDescription: resources.createOrderTemplateDescription,

            defaultOrderTemplateName: resources.defaultOrderTemplateName,

            orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
        };
    }

    private _generateRenameOrderTemplateDialogResources(resources: IOrderTemplateListResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.renameOrderTemplateTitleText,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
            orderTemplateDescription: '',
            defaultOrderTemplateName: resources.defaultOrderTemplateName,
            orderTemplateButtonLabel: resources.renameOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
        };
    }

    private readonly _isChannelMultipleCatalogsFeatureEnabled = (): boolean => {
        let isMultipleCatalogFeatureEnabledInHq = false;
        if (this.props.data.featureState) {
            isMultipleCatalogFeatureEnabledInHq =
                this.props.data.featureState.result?.find(
                    feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                )?.IsEnabled || false;
        }
        return isMultipleCatalogFeatureEnabledInHq;
    };

    private _renderOrderTemplateList(orderTemplates: OrderTemplate[]): ISingleOrderTemplateViewProps[] {
        const { context, telemetry, data } = this.props;
        const { imageSettings, enableImageProductLink } = this.props.config;
        const isB2bAdmin = data.businessPartnerUser?.result?.IsAdmin ?? false;

        return orderTemplates.map((orderTemplate: OrderTemplate, index) => {
            const currentTemplateProps: ISingleOrderTemplateProps = {
                orderTemplateState: this.state,
                Id: orderTemplate.productList.Id,
                name: orderTemplate.productList.Name,
                orderTemplate,
                context,
                imageSettings,
                enableImageProductLink,
                removeFromOrderTemplateListText: 'remove',
                addToBagText: this.props.resources.addToBagButtonText,
                index,
                itemKey: orderTemplate.productList.Id,
                telemetry,
                moduleId: this.props.id,
                moduleTypeName: this.props.typeName,
                businessPartnerUser: data.businessPartnerUser?.result,
                isShared: orderTemplate.productList.IsShared ?? false,
                isB2bAdmin: isB2bAdmin,
                isB2bContact: (this.props.context.request.user as { isB2bContact?: boolean }).isB2bContact === true,
                handlers: {
                    onRemoveTemplate: this._onRemoveFromOrderTemplateList,
                    onDismiss: this._onDismiss,
                    onShareClick: this._toggleTemplateSharedState,
                    onRenameTemplate: this._renameTemplate,
                    onShareTemplate: this._toggleTemplateSharedState
                },
                resources: {
                    closeWindowButtonText: this.props.resources.closeWindowButtonText,
                    addToCartFailureMessage: this.props.resources.addToCartFailureMessage,
                    addToCartSuccessMessage: this.props.resources.addToCartSuccessMessage,
                    addToCartProcessMessage: this.props.resources.addToCartProcessMessage,
                    orderTemplateErrorMessage: this.props.resources.orderTemplateErrorMessage,
                    linesAddedToCartDialogResources: {
                        viewCartButtonText: this.props.resources.viewCartButtonText,
                        orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
                        linesAddedToCartHeaderItemsOneText: this.props.resources.linesAddedToCartHeaderItemsOneText,
                        linesAddedToCartHeaderLinesOneText: this.props.resources.linesAddedToCartHeaderLinesOneText,
                        linesAddedToCartHeaderLinesFormatText: this.props.resources.linesAddedToCartHeaderLinesFormatText,
                        linesAddedToCartHeaderMessageText: this.props.resources.linesAddedToCartHeaderMessageText
                    },
                    orderTemplateCreatedByText: this.props.resources.orderTemplateCreatedByText,
                    orderTemplateSharedByText: this.props.resources.orderTemplateSharedByText,
                    orderTemplateYouText: this.props.resources.orderTemplateYouText,
                    orderTemplateForText: this.props.resources.orderTemplateForText,
                    orderTemplateModifiedByText: this.props.resources.orderTemplateModifiedByText,
                    shareOrderTemplateDialogResources: {
                        shareOrderTemplateHeader: this.props.resources.shareOrderTemplateHeader,
                        unShareOrderTemplateHeader: this.props.resources.unShareOrderTemplateHeader,
                        shareOrderTemplatesDescription: this.props.resources.shareOrderTemplatesDescription,
                        unshareOrderTemplatesDescription: this.props.resources.unshareOrderTemplatesDescription,
                        shareOrderTemplateButtonText: this.props.resources.shareOrderTemplateButtonText,
                        unshareOrderTemplateButtonText: this.props.resources.unshareOrderTemplateButtonText,
                        cancelOrderTemplateShareButtonText: this.props.resources.cancelOrderTemplateShareButtonText
                    },
                    templateShareButtonText: this.props.resources.templateShareButtonText,
                    templateUnshareButtonText: this.props.resources.templateUnshareButtonText,
                    renameTemplateButtonText: this.props.resources.renameTemplateButtonText,
                    deleteTemplateButtonText: this.props.resources.deleteTemplateButtonText,
                    renameOrderTemplateDialogStrings: this._generateRenameOrderTemplateDialogResources(this.props.resources)
                },
                isMultipleCatalogFeatureEnabledInHq: this._isChannelMultipleCatalogsFeatureEnabled()
            };

            return OrderTemplateRender(currentTemplateProps)!;
        });
    }

    private readonly _onDismiss = () => {
        this.setState({
            isOpen: false
        });
    };

    private readonly _reloadPage = async () => {
        window.location.reload();
    };

    private readonly _onRemoveFromOrderTemplateList = (productListId: string): void | undefined => {
        const { context, data, telemetry, resources } = this.props;
        const { orderTemplates } = data;

        if (context && context.actionContext) {
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken && orderTemplates.result && orderTemplates.result[0] && productListId) {
                const orderTemplate = orderTemplates.result.find(p => p.productList.Id === productListId);
                if (orderTemplate) {
                    deleteAsync({ callerContext: context.actionContext }, orderTemplate.productList)
                        .then()
                        .catch((error: string) => {
                            telemetry.warning('[order-template-list] Unable to delete order template.');
                            telemetry.error(error);
                            this.setState({
                                itemKey: productListId,
                                headerStatusMessage: resources.deleteOrderTemplateFailedText,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        })
                        .then(this._reloadPage)
                        .catch((error: string) => {
                            telemetry.warning('[order-template-list] Unable to delete order template.');
                            telemetry.error(error);
                        });
                } else {
                    telemetry.warning('[order-template-list] Failed to delete order template. Order template was not found');
                }
            } else {
                telemetry.warning('[order-template-list] Failed to delete order template.');
            }
        }
    };

    private readonly _renderFilterButtons = ({
        className,
        resources: { orderTemplateFilterAllLabel, orderTemplateFilterMyTemplatesLabel, orderTemplateFilterSharedTemplatesLabel }
    }: {
        className: string;
        resources: IOrderTemplateListResources;
    }) => {
        if (!this.state.isContactModelOrderTemplate) {
            return;
        }

        const buttons = [
            { orderTemplateFilter: '0', label: orderTemplateFilterAllLabel },
            { orderTemplateFilter: '1', label: orderTemplateFilterMyTemplatesLabel },
            { orderTemplateFilter: '2', label: orderTemplateFilterSharedTemplatesLabel }
        ];

        return (
            <div className='ms-order-template-list__filter-button-container'>
                {buttons.map(({ label, orderTemplateFilter }) => (
                    <Button
                        key={orderTemplateFilter}
                        className={`${className}-${this.state.selectedFilter === orderTemplateFilter ? 'selected' : 'outline'}`}
                        onClick={() => this._filterOrderTemplates(orderTemplateFilter)}
                    >
                        {label}
                    </Button>
                ))}
            </div>
        );
    };

    private readonly _filterOrderTemplates = async (orderTemplateFilter: string) => {
        const { context, telemetry } = this.props;
        try {
            const orderTemplatesData = await getOrderTemplatesPreview(
                createGetOrderTemplatesPreviewInput(context.actionContext, orderTemplateFilter),
                context.actionContext
            );
            this.setState({ selectedFilter: orderTemplateFilter, orderTemplatesData });
        } catch (error) {
            telemetry.error('Unable to filter order templates.', error);
        }
    };

    private readonly _toggleTemplateSharedState = async (_updatedProductList: ProductList): Promise<void> => {
        window.location.reload();
    };

    private readonly _renameTemplate = async (_template: ProductList): Promise<void> => {
        window.location.reload();
        return Promise.resolve();
    };
}

export default OrderTemplateList;
